import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Calendar } from '../../components/ui/calendar';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Star, Trash2, AlertTriangle, Info, Loader2, Bell, Eye, XCircle, CheckCircle2, MapPin, Clock, Plus, Wrench, User, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import { paymentTerms, refundPolicy } from '../../mock';
import api from '../../services/api';
import { useCalendarHolidays } from '../../hooks/useHolidays';
import ProjectDetailModal from '../../components/ProjectDetailModal';
import QuotationApprovalDialog from '../../components/QuotationApprovalDialog';
import Sv2ApprovalDialog from '../../components/Sv2ApprovalDialog';
import { StageName, QuotationStatus, PaymentStatus } from '../../constants/stageConstants';

const EnhancedDashboard = () => {
  const navigate = useNavigate();
  const { holidayDates, isHoliday } = useCalendarHolidays();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return dateString;
    }
  };
  const [activeFilter, setActiveFilter] = useState('all');
  const [ratingsDialogOpen, setRatingsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [paymentInfoOpen, setPaymentInfoOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [projectForApproval, setProjectForApproval] = useState(null);
  const [projectDetailOpen, setProjectDetailOpen] = useState(false);
  const [selectedProjectDetail, setSelectedProjectDetail] = useState(null);
  const [loadingProjectId, setLoadingProjectId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [quotationApprovalOpen, setQuotationApprovalOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [loadingQuotation, setLoadingQuotation] = useState(false);
  const [sv2ApprovalOpen, setSv2ApprovalOpen] = useState(false);
  const [sv2ApprovalProject, setSv2ApprovalProject] = useState(null);

  // Sign-off states
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [signoffProject, setSignoffProject] = useState(null);
  const [signoffDetails, setSignoffDetails] = useState(null);
  const [signoffLoading, setSignoffLoading] = useState(false);
  const [signoffRating, setSignoffRating] = useState(0);
  const [signoffHoverRating, setSignoffHoverRating] = useState(0);
  const [signoffNotes, setSignoffNotes] = useState('');
  const [signoffSubmitting, setSignoffSubmitting] = useState(false);
  const [signoffItemPhotos, setSignoffItemPhotos] = useState({});
  const [signoffPhotosExpanded, setSignoffPhotosExpanded] = useState({});
  const [signoffEnlargedPhoto, setSignoffEnlargedPhoto] = useState(null);

  // OEM profile (self) states
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileForm, setProfileForm] = useState({
    contactPerson: '',
    contactPersonMobile: '',
    contactPersonDesignation: '',
    contactPersonEmail: '',
    address: '',
    billingAddress: '',
  });

  // Fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await api.request('/api/dashboard/oem');
      console.log('OEM Dashboard Response:', response);
      console.log('Projects:', response.projects);
      // Use projects from the response
      const transformedProjects = (response?.projects || []).map(p => ({
        id: p.id,
        clientName: p.clientName,
        clientPhone: p.clientPhone,
        clientEmail: p.clientEmail,
        location: p.location,
        siteAddress: p.siteAddress,
        sqft: p.sqft,
        type: p.type,
        shape: p.shape,
        status: p.status,
        preferredDate: p.preferredDate,
        scheduledDate: p.scheduledDate,
        startedDate: p.startedDate,
        deliveryDate: p.deliveryDate,
        completedDate: p.completedDate,
        installerTeam: p.installerTeam,
        progress: p.progress,
        rating: p.rating,
        quoteAmount: p.quoteAmount,
        supervisorName: p.supervisorName,
        supervisorPhone: p.supervisorPhone,
        createdAt: p.createdAt,
        // Site Visit Info
        siteVisit1Completed: p.siteVisit1Completed,
        siteVisit1Date: p.siteVisit1Date,
        siteVisit2Completed: p.siteVisit2Completed,
        siteVisit2Date: p.siteVisit2Date,
        // Approval Info
        oemApprovalStatus: p.oemApprovalStatus,
        originalQuote: p.originalQuote,
        revisedQuote: p.revisedQuote,
        scheduleChangeReason: p.scheduleChangeReason,
        reworkRequired: p.reworkRequired,
        reworkDetails: p.reworkDetails,
        hardwareListUploaded: p.hardwareListUploaded,
        hasPendingAdditionalWork: p.hasPendingAdditionalWork
      }));
      setProjects(transformedProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load projects',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── OEM self-profile handlers ───
  const handleOpenProfile = async () => {
    setProfileDialogOpen(true);
    setProfileLoading(true);
    try {
      const data = await api.getMyOem();
      setProfileData(data);
      setProfileForm({
        contactPerson: data?.contactPerson || '',
        contactPersonMobile: data?.contactPersonMobile || '',
        contactPersonDesignation: data?.contactPersonDesignation || '',
        contactPersonEmail: data?.contactPersonEmail || '',
        address: data?.address || '',
        billingAddress: data?.billingAddress || '',
      });
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to load profile', variant: 'destructive' });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileForm.contactPerson?.trim()) {
      toast({ title: 'Contact person required', description: 'Please enter a contact person name.', variant: 'destructive' });
      return;
    }
    setProfileSaving(true);
    try {
      const updated = await api.updateMyOem(profileForm);
      setProfileData(updated);
      toast({ title: 'Profile updated', description: 'Your contact details have been saved.' });
      setProfileDialogOpen(false);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to save profile', variant: 'destructive' });
    } finally {
      setProfileSaving(false);
    }
  };

  // ─── Sign-off handlers ───
  const handleOpenSignoff = async (project) => {
    setSignoffProject(project);
    setSignoffRating(0);
    setSignoffHoverRating(0);
    setSignoffNotes('');
    setSignoffItemPhotos({});
    setSignoffPhotosExpanded({});
    setSignoffEnlargedPhoto(null);
    setSignoffDialogOpen(true);
    setSignoffLoading(true);
    try {
      const details = await api.getOemSignoffDetails(project.id);
      setSignoffDetails(details);
    } catch (error) {
      console.error('Error loading signoff details:', error);
      toast({ title: 'Error', description: 'Failed to load sign-off details', variant: 'destructive' });
    } finally {
      setSignoffLoading(false);
    }
  };

  const loadSignoffItemPhotos = async (projectId, responseId) => {
    if (signoffItemPhotos[responseId]) {
      setSignoffPhotosExpanded(prev => ({ ...prev, [responseId]: !prev[responseId] }));
      return;
    }
    try {
      const token = localStorage.getItem('alignworks_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const docsRes = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents?referenceId=${responseId}`, { headers });
      if (!docsRes.ok) throw new Error('Failed to fetch documents');
      const data = await docsRes.json();
      const docsList = data.documents || data || [];
      const photos = [];
      for (const doc of docsList) {
        const streamRes = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents/${doc.id}/stream`, { headers });
        if (streamRes.ok) {
          const blob = await streamRes.blob();
          photos.push({ id: doc.id, url: URL.createObjectURL(blob), fileName: doc.fileName });
        }
      }
      setSignoffItemPhotos(prev => ({ ...prev, [responseId]: photos }));
      setSignoffPhotosExpanded(prev => ({ ...prev, [responseId]: true }));
    } catch (error) {
      console.error('Error loading signoff photos:', error);
      toast({ title: 'Error', description: 'Failed to load photos', variant: 'destructive' });
    }
  };

  const handleApproveSignoff = async () => {
    if (signoffRating < 1) {
      toast({ title: 'Rating Required', description: 'Please select a rating (1-5)', variant: 'destructive' });
      return;
    }
    setSignoffSubmitting(true);
    try {
      await api.oemApproveSignoff(signoffProject.id, signoffRating, signoffNotes);
      toast({ title: 'Sign-Off Approved', description: `Project rated ${signoffRating}/5 and moved to admin closure` });
      setSignoffDialogOpen(false);
      fetchProjects();
    } catch (error) {
      console.error('Error approving signoff:', error);
      toast({ title: 'Error', description: error.message || 'Failed to approve sign-off', variant: 'destructive' });
    } finally {
      setSignoffSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      [StageName.NewProject]: 'bg-slate-100 text-slate-700 border-slate-300',
      [StageName.QuotationApprovalPending]: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      [StageName.QuotationApproved]: 'bg-green-100 text-green-700 border-green-300',
      [StageName.QuotePending]: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'Quote Sent': 'bg-yellow-100 text-yellow-700 border-yellow-300',

      [StageName.Sv1Scheduled]: 'bg-amber-100 text-amber-700 border-amber-300',
      [StageName.Sv1Completed]: 'bg-sky-100 text-sky-700 border-sky-300',
      [StageName.Sv1Pending]: 'bg-amber-100 text-amber-700 border-amber-300',
      'SV1 Complete': 'bg-sky-100 text-sky-700 border-sky-300',

      [StageName.Sv2Proposed]: 'bg-amber-100 text-amber-700 border-amber-300',
      [StageName.Sv2Scheduled]: 'bg-orange-100 text-orange-700 border-orange-300',
      [StageName.Sv2Completed]: 'bg-indigo-100 text-indigo-700 border-indigo-300',
      [StageName.Sv2Pending]: 'bg-orange-100 text-orange-700 border-orange-300',
      'SV2 Complete': 'bg-indigo-100 text-indigo-700 border-indigo-300',

      [StageName.InstallationScheduled]: 'bg-blue-100 text-blue-700 border-blue-300',
      [StageName.InstallationInProgress]: 'bg-purple-100 text-purple-700 border-purple-300',
      [StageName.InstallationCompleted]: 'bg-green-100 text-green-700 border-green-300',
      [StageName.ClientReview]: 'bg-cyan-100 text-cyan-700 border-cyan-300',
      [StageName.Scheduled]: 'bg-blue-100 text-blue-700 border-blue-300',
      [StageName.InProgress]: 'bg-purple-100 text-purple-700 border-purple-300',

      [StageName.PendingOemSignoff]: 'bg-pink-100 text-pink-700 border-pink-300',
      [StageName.PendingAdminClosure]: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      [StageName.ProjectCompleted]: 'bg-green-100 text-green-700 border-green-300',
      [StageName.Completed]: 'bg-green-100 text-green-700 border-green-300',
      [StageName.Delivered]: 'bg-emerald-100 text-emerald-700 border-emerald-300',

      [StageName.OnHold]: 'bg-red-100 text-red-700 border-red-300',
      [StageName.Cancelled]: 'bg-gray-100 text-gray-700 border-gray-300',
      [StageName.PendingApproval]: 'bg-pink-100 text-pink-700 border-pink-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const getCompletionPercentage = (project) => {
    if (project.progress !== undefined) return project.progress;
    return null;
  };

  const toDateStr = (val) => {
    if (!val) return null;
    if (val.includes('T')) return val.split('T')[0];
    return val.length >= 10 ? val.substring(0, 10) : null;
  };

  const getDayStyles = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const projectsOnDate = projects.filter(p => {
      return toDateStr(p.scheduledDate) === dateStr ||
        toDateStr(p.completedDate) === dateStr ||
        toDateStr(p.siteVisit1Date) === dateStr ||
        toDateStr(p.siteVisit2Date) === dateStr;
    });

    if (projectsOnDate.length === 0) return {};

    const hasCompleted = projectsOnDate.some(p => p.status === StageName.ProjectCompleted || toDateStr(p.completedDate) === dateStr);
    const hasInProgress = projectsOnDate.some(p => p.status === StageName.InstallationInProgress);
    const hasScheduled = projectsOnDate.some(p =>
      toDateStr(p.scheduledDate) === dateStr ||
      toDateStr(p.siteVisit1Date) === dateStr ||
      toDateStr(p.siteVisit2Date) === dateStr
    );

    if (hasCompleted) return { backgroundColor: '#10b981', color: 'white', borderRadius: '50%' };
    if (hasInProgress) return { backgroundColor: '#ef4444', color: 'white', borderRadius: '50%' };
    if (hasScheduled) return { backgroundColor: '#3b82f6', color: 'white', borderRadius: '50%' };
    return {};
  };

  const handleDeleteProject = async () => {
    if (projectToDelete) {
      try {
        await api.deleteProject(projectToDelete.id);
        setProjects(projects.filter(p => p.id !== projectToDelete.id));
        toast({
          title: 'Project Deleted',
          description: `Project for ${projectToDelete.clientName} has been deleted.`
        });
        setDeleteDialogOpen(false);
        setProjectToDelete(null);
      } catch (error) {
        console.error('Error deleting project:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete project',
          variant: 'destructive'
        });
      }
    }
  };

  const handleViewProjectDetail = async (project) => {
    setLoadingProjectId(project.id);
    try {
      const response = await api.request(`/api/projects/${project.id}/complete`);
      setSelectedProjectDetail(response);
      setProjectDetailOpen(true);
    } catch (error) {
      console.error('Error loading project details:', error);
      toast({
        title: "Error",
        description: "Failed to load project details",
        variant: "destructive"
      });
    } finally {
      setLoadingProjectId(null);
    }
  };

  const handleApproveChanges = async (approved) => {
    if (!projectForApproval) return;

    try {
      await api.oemApproveChanges(projectForApproval.id, approved);
      toast({
        title: approved ? 'Changes Approved' : 'Changes Rejected',
        description: approved
          ? 'Quote and schedule changes have been approved'
          : 'Changes have been rejected. Admin will be notified.'
      });
      setApprovalDialogOpen(false);
      setProjectForApproval(null);
      fetchProjects();
    } catch (error) {
      console.error('Error handling approval:', error);
      toast({
        title: 'Error',
        description: 'Failed to process approval',
        variant: 'destructive'
      });
    }
  };

  const canDeleteProject = (project) => {
    return project.status === StageName.QuotePending ||
      (project.paymentStatus === PaymentStatus.Pending && !project.bookingPaid);
  };

  const handleFilterChange = (type) => {
    setActiveFilter(type);
  };

  const getFilteredProjects = () => {
    switch (activeFilter) {
      case 'in-progress':
        return projects.filter(p =>
          p.status !== StageName.ProjectCompleted &&
          p.status !== StageName.Cancelled &&
          p.status !== StageName.OnHold
        );
      case 'completed':
        return projects.filter(p => p.status === StageName.ProjectCompleted);
      case 'all':
      default:
        return projects;
    }
  };

  const recentRatings = projects.filter(p => p.rating).map(p => ({
    id: p.id,
    project: p.clientName,
    client: p.clientName,
    rating: p.rating,
    comment: 'Great work by the team!',
    date: p.completedDate
  }));

  const scheduledProjects = projects.filter(p => p.scheduledDate || p.siteVisit1Date || p.siteVisit2Date);
  const inProgressProjects = projects.filter(p =>
    p.status !== StageName.ProjectCompleted &&
    p.status !== StageName.Cancelled &&
    p.status !== StageName.OnHold
  );
  const completedProjects = projects.filter(p => p.status === StageName.ProjectCompleted);
  const avgRating = completedProjects.length > 0
    ? (completedProjects.reduce((sum, p) => sum + (p.rating || 0), 0) / completedProjects.filter(p => p.rating).length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto" />
          <p className="mt-2 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">OEM Dashboard</h1>
              <p className="text-gray-600 mt-1">Manage your installation projects</p>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <Button
                onClick={() => navigate('/admin/projects/create')}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Project
              </Button>
              <Button
                variant="outline"
                className="border-gray-300"
                onClick={() => setPaymentInfoOpen(true)}
              >
                <Info className="w-4 h-4 mr-2" />
                Payment Terms
              </Button>
              <Button
                variant="outline"
                className="border-gray-300"
                onClick={handleOpenProfile}
              >
                <User className="w-4 h-4 mr-2" />
                My Profile
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card
            className={`cursor-pointer hover:shadow-lg transition-all ${activeFilter === 'all' ? 'ring-2 ring-teal-500 shadow-lg' : ''}`}
            onClick={() => handleFilterChange('all')}
          >
            <CardContent className="p-3 sm:p-6">
              <div className="text-xs sm:text-sm font-medium text-gray-600">Total Projects</div>
              <div className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1 sm:mt-2">{projects.length}</div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer hover:shadow-lg transition-all ${activeFilter === 'in-progress' ? 'ring-2 ring-teal-500 shadow-lg' : ''}`}
            onClick={() => handleFilterChange('in-progress')}
          >
            <CardContent className="p-3 sm:p-6">
              <div className="text-xs sm:text-sm font-medium text-gray-600">In Progress</div>
              <div className="text-2xl sm:text-3xl font-bold text-purple-600 mt-1 sm:mt-2">{inProgressProjects.length}</div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer hover:shadow-lg transition-all ${activeFilter === 'completed' ? 'ring-2 ring-teal-500 shadow-lg' : ''}`}
            onClick={() => handleFilterChange('completed')}
          >
            <CardContent className="p-3 sm:p-6">
              <div className="text-xs sm:text-sm font-medium text-gray-600">Completed</div>
              <div className="text-2xl sm:text-3xl font-bold text-green-600 mt-1 sm:mt-2">{completedProjects.length}</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setRatingsDialogOpen(true)}>
            <CardContent className="p-3 sm:p-6">
              <div className="text-xs sm:text-sm font-medium text-gray-600">Avg Rating</div>
              <div className="flex items-center mt-1 sm:mt-2">
                <span className="text-2xl sm:text-3xl font-bold text-teal-600">{avgRating || 'N/A'}</span>
                {avgRating > 0 && <Star className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500 ml-2 fill-yellow-500" />}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Approvals Section */}
        {projects.filter(p => p.oemApprovalStatus === 'pending').length > 0 && (
          <Card className="mb-8 border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <Bell className="w-5 h-5" />
                Pending Approvals
              </CardTitle>
              <CardDescription>Projects requiring your approval for quote/schedule changes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {projects.filter(p => p.oemApprovalStatus === 'pending').map(project => (
                  <div key={project.id} className="p-4 bg-white rounded-lg border border-orange-200 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">{project.clientName}</h4>
                        <p className="text-sm text-gray-500">{project.location} • {project.sqft} sq.ft</p>
                        {project.scheduleChangeReason && (
                          <p className="text-sm text-orange-600 mt-1">
                            <AlertTriangle className="w-3 h-3 inline mr-1" />
                            Reason: {project.scheduleChangeReason}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        {project.revisedQuote && (
                          <div className="mb-2">
                            <p className="text-xs text-gray-500">Revised Quote</p>
                            <p className="font-bold text-lg text-orange-600">₹{project.revisedQuote?.toLocaleString()}</p>
                          </div>
                        )}
                        <Button
                          size="sm"
                          className="bg-orange-600 hover:bg-orange-700"
                          onClick={() => {
                            setProjectForApproval(project);
                            setApprovalDialogOpen(true);
                          }}
                          data-testid={`review-approval-${project.id}`}
                        >
                          Review Changes
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ratings Dialog */}
        <Dialog open={ratingsDialogOpen} onOpenChange={setRatingsDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Recent Ratings</DialogTitle>
              <DialogDescription>Last 5 project ratings</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {recentRatings.slice(0, 5).map(rating => (
                <div key={rating.id} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{rating.project}</p>
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < rating.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{rating.client}</p>
                  <p className="text-xs text-gray-400 mt-1">{rating.date}</p>
                </div>
              ))}
              {recentRatings.length === 0 && (
                <p className="text-center text-gray-500 py-4">No ratings yet</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Delete Project
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete the project for "{projectToDelete?.clientName}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mt-4">
              <p className="text-sm text-yellow-800">
                <strong>Refund Policy:</strong> If any payment was made, refunds will be processed as per policy.
              </p>
            </div>
            <div className="flex gap-3 mt-4">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleDeleteProject}>
                Delete Project
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* OEM My Profile Dialog */}
        <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
            <DialogHeader className="pr-8">
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-teal-600 shrink-0" />
                My Profile
              </DialogTitle>
              <DialogDescription>
                Update your contact details. Company name, GST/PAN, logo and account status can only be changed by the administrator.
              </DialogDescription>
            </DialogHeader>

            {profileLoading ? (
              <div className="py-10 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
              </div>
            ) : profileData ? (
              <div className="mt-4 space-y-6">
                {/* Read-only company info */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-4">
                    {profileData.logoUrl ? (
                      <img
                        src={profileData.logoUrl}
                        alt="Company logo"
                        className="w-16 h-16 rounded object-contain bg-white border border-gray-200"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded bg-gray-200 flex items-center justify-center text-gray-500 text-xs">
                        No logo
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-lg font-semibold text-gray-900 truncate">
                        {profileData.companyName || '—'}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                        <div><span className="text-gray-500">GST:</span> {profileData.gstNumber || '—'}</div>
                        <div><span className="text-gray-500">PAN:</span> {profileData.panNumber || '—'}</div>
                        <div>
                          <span className="text-gray-500">Status:</span>{' '}
                          <span className={profileData.isActive ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                            {profileData.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Editable contact info */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Contact Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="profile-contactPerson">Contact Person *</Label>
                      <Input
                        id="profile-contactPerson"
                        value={profileForm.contactPerson}
                        onChange={(e) => setProfileForm((f) => ({ ...f, contactPerson: e.target.value }))}
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="profile-contactPersonDesignation">Designation</Label>
                      <Input
                        id="profile-contactPersonDesignation"
                        value={profileForm.contactPersonDesignation}
                        onChange={(e) => setProfileForm((f) => ({ ...f, contactPersonDesignation: e.target.value }))}
                        placeholder="e.g. Operations Manager"
                      />
                    </div>
                    <div>
                      <Label htmlFor="profile-contactPersonMobile">Mobile</Label>
                      <Input
                        id="profile-contactPersonMobile"
                        value={profileForm.contactPersonMobile}
                        onChange={(e) => setProfileForm((f) => ({ ...f, contactPersonMobile: e.target.value }))}
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                    <div>
                      <Label htmlFor="profile-contactPersonEmail">Email</Label>
                      <Input
                        id="profile-contactPersonEmail"
                        type="email"
                        value={profileForm.contactPersonEmail}
                        onChange={(e) => setProfileForm((f) => ({ ...f, contactPersonEmail: e.target.value }))}
                        placeholder="contact@example.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Addresses */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Addresses</h4>
                  <div>
                    <Label htmlFor="profile-address">Address</Label>
                    <Textarea
                      id="profile-address"
                      rows={2}
                      value={profileForm.address}
                      onChange={(e) => setProfileForm((f) => ({ ...f, address: e.target.value }))}
                      placeholder="Office address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="profile-billingAddress">Billing Address</Label>
                    <Textarea
                      id="profile-billingAddress"
                      rows={2}
                      value={profileForm.billingAddress}
                      onChange={(e) => setProfileForm((f) => ({ ...f, billingAddress: e.target.value }))}
                      placeholder="Billing address"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setProfileDialogOpen(false)}
                    disabled={profileSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveProfile}
                    disabled={profileSaving}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    {profileSaving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="py-10 text-center text-gray-500">No profile data available.</div>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Terms Dialog */}
        <Dialog open={paymentInfoOpen} onOpenChange={setPaymentInfoOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Payment Terms & Refund Policy</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 mt-4">
              <div>
                <p className="font-medium text-gray-900 mb-3">Payment Schedule</p>
                <div className="space-y-3">
                  <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-teal-900">Booking Payment</span>
                      <span className="text-teal-700 font-bold">40%</span>
                    </div>
                    <p className="text-sm text-teal-700 mt-1">{paymentTerms.booking.description}</p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-blue-900">Second Payment</span>
                      <span className="text-blue-700 font-bold">40%</span>
                    </div>
                    <p className="text-sm text-blue-700 mt-1">{paymentTerms.second.description}</p>
                  </div>
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-green-900">Final Payment</span>
                      <span className="text-green-700 font-bold">20%</span>
                    </div>
                    <p className="text-sm text-green-700 mt-1">{paymentTerms.final.description}</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="font-medium text-gray-900 mb-3">Refund Policy</p>
                <div className="space-y-2">
                  {refundPolicy.map((policy, idx) => (
                    <div key={idx} className={`p-3 rounded-lg ${policy.refundPercentage === 0 ? 'bg-red-50 border border-red-200' :
                      policy.refundPercentage >= 80 ? 'bg-green-50 border border-green-200' :
                        'bg-yellow-50 border border-yellow-200'
                      }`}>
                      <div className="flex justify-between items-center">
                        <span className={`font-medium ${policy.refundPercentage === 0 ? 'text-red-900' :
                          policy.refundPercentage >= 80 ? 'text-green-900' : 'text-yellow-900'
                          }`}>
                          {policy.type === 'reschedule' ? 'Rescheduling' : `${policy.daysBeforeBooking} days before`}
                        </span>
                        <span className={`font-bold ${policy.refundPercentage === 0 ? 'text-red-700' :
                          policy.refundPercentage >= 80 ? 'text-green-700' : 'text-yellow-700'
                          }`}>
                          {policy.refundPercentage}% {policy.type === 'reschedule' ? 'adjustable' : 'refund'}
                        </span>
                      </div>
                      <p className={`text-sm mt-1 ${policy.refundPercentage === 0 ? 'text-red-700' :
                        policy.refundPercentage >= 80 ? 'text-green-700' : 'text-yellow-700'
                        }`}>
                        {policy.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Section */}
          <Card className="lg:col-span-1 order-2 lg:order-none">
            <CardHeader>
              <CardTitle className="text-lg">Installation Calendar</CardTitle>
              <CardDescription>
                <span className="flex gap-3 mt-1">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                    <span className="text-xs">Scheduled</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    <span className="text-xs">In Progress</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    <span className="text-xs">Completed</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-orange-400"></span>
                    <span className="text-xs">Holiday</span>
                  </span>
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-md border w-full"
                classNames={{
                  head_cell: "text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem]",
                  cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 flex-1 [&:has([aria-selected])]:rounded-md",
                  day: "h-8 w-full p-0 font-normal"
                }}
                modifiersStyles={{
                  selected: { backgroundColor: '#14b8a6' }
                }}
                modifiers={{
                  holiday: holidayDates
                }}
                modifiersClassNames={{
                  holiday: 'holiday-day'
                }}
                components={{
                  Day: ({ date, displayMonth, ...props }) => {
                    const styles = getDayStyles(date);
                    const holiday = isHoliday(date);
                    const isSelected = selectedDate && format(selectedDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
                    const mergedStyles = holiday
                      ? { ...styles, backgroundColor: styles.backgroundColor || '#fed7aa', border: '2px solid #f97316' }
                      : styles;
                    if (isSelected) {
                      mergedStyles.backgroundColor = '#14b8a6';
                      mergedStyles.color = 'white';
                      mergedStyles.borderRadius = '50%';
                    }
                    return (
                      <button
                        {...props}
                        style={mergedStyles}
                        className={`${props.className} w-8 h-8 font-normal cursor-pointer hover:opacity-80`}
                        title={holiday ? 'Holiday' : undefined}
                        onClick={() => setSelectedDate(date)}
                      >
                        {date.getDate()}
                      </button>
                    );
                  }
                }}
              />
              {/* Selected date projects */}
              {selectedDate && (() => {
                const dateStr = format(selectedDate, 'yyyy-MM-dd');
                const projectsOnDate = projects.filter(p =>
                  toDateStr(p.scheduledDate) === dateStr ||
                  toDateStr(p.completedDate) === dateStr ||
                  toDateStr(p.siteVisit1Date) === dateStr ||
                  toDateStr(p.siteVisit2Date) === dateStr
                );
                return projectsOnDate.length > 0 ? (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      {format(selectedDate, 'MMM dd, yyyy')}
                    </p>
                    {projectsOnDate.map(project => (
                      <div
                        key={project.id}
                        className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-sm cursor-pointer hover:bg-blue-100"
                        onClick={() => navigate(`/projects/${project.id}/view`)}
                      >
                        <p className="font-medium text-gray-900">{project.clientName}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${getStatusColor(project.status)}`}>
                            {project.status}
                          </span>
                          {toDateStr(project.siteVisit1Date) === dateStr && <span className="text-xs text-blue-600">SV1</span>}
                          {toDateStr(project.siteVisit2Date) === dateStr && <span className="text-xs text-orange-600">SV2</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}

              {/* Upcoming list */}
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-gray-700">Upcoming Installations</p>
                {scheduledProjects.slice(0, 3).map(project => {
                  const nextDate = project.scheduledDate || project.siteVisit1Date || project.siteVisit2Date;
                  return (
                    <div key={project.id} className="p-2 bg-gray-50 rounded-lg text-sm cursor-pointer hover:bg-gray-100" onClick={() => navigate(`/projects/${project.id}/view`)}>
                      <p className="font-medium">{project.clientName}</p>
                      <p className="text-gray-500 text-xs">{nextDate ? formatDate(nextDate) : 'Date pending'}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Projects List */}
          <Card className="lg:col-span-2 order-1 lg:order-none">
            <CardHeader>
              <CardTitle className="text-lg">
                {activeFilter === 'all' && 'All Projects'}
                {activeFilter === 'in-progress' && 'In Progress Projects'}
                {activeFilter === 'completed' && 'Completed Projects'}
                {activeFilter === 'total' && 'All Projects'}
              </CardTitle>
              <CardDescription>
                {activeFilter === 'all' ? 'All your installation projects' : `Showing ${getFilteredProjects().length} ${activeFilter === 'in-progress' ? 'in progress' : 'completed'} project(s)`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {getFilteredProjects().map(project => {
                  const completion = getCompletionPercentage(project);
                  return (
                    <div
                      key={project.id}
                      className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/projects/${project.id}/view`)}
                      data-testid={`project-card-${project.id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-gray-900 break-words">{project.clientName}</h3>
                            <span className={`text-xs px-2 py-1 rounded-full border whitespace-nowrap ${getStatusColor(project.status)}`}>
                              {project.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {formatDate(project.preferredDate || project.scheduledDate)}
                            </span>
                          </div>
                        </div>
                        <div className="text-left sm:text-right flex items-start gap-2 shrink-0">
                          <div>
                            <p className="text-lg font-semibold text-gray-900">{project.sqft} sq.ft</p>
                            {project.quoteAmount && (
                              <p className="text-sm font-medium text-teal-600">₹{project.quoteAmount.toLocaleString()}</p>
                            )}
                            {project.status === StageName.QuotationApprovalPending && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 mt-2"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setLoadingQuotation(true);
                                  try {
                                    const response = await api.get(`/api/projects/${project.id}/quotations`);
                                    const pendingQuotation = response.quotations?.find(q => q.status === QuotationStatus.Sent);
                                    if (pendingQuotation) {
                                      setSelectedQuotation({ ...pendingQuotation, projectId: project.id });
                                      setQuotationApprovalOpen(true);
                                    } else {
                                      toast({ title: 'No pending quotation found', variant: 'destructive' });
                                    }
                                  } catch (error) {
                                    console.error('Error fetching quotation:', error);
                                    toast({ title: 'Failed to load quotation', variant: 'destructive' });
                                  } finally {
                                    setLoadingQuotation(false);
                                  }
                                }}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                            )}
                            {project.hasPendingAdditionalWork && (
                              <Button
                                size="sm"
                                className="bg-orange-600 hover:bg-orange-700 mt-2"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setLoadingQuotation(true);
                                  try {
                                    const response = await api.get(`/api/projects/${project.id}/quotations`);
                                    const pendingQuotation = response.quotations?.find(
                                      q => q.status === QuotationStatus.Sent && q.quotationType === 'additional_work'
                                    );
                                    if (pendingQuotation) {
                                      setSelectedQuotation({ ...pendingQuotation, projectId: project.id });
                                      setQuotationApprovalOpen(true);
                                    } else {
                                      toast({ title: 'No pending additional work quotation found', variant: 'destructive' });
                                    }
                                  } catch (error) {
                                    console.error('Error fetching quotation:', error);
                                    toast({ title: 'Failed to load quotation', variant: 'destructive' });
                                  } finally {
                                    setLoadingQuotation(false);
                                  }
                                }}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Approve Extra Work
                              </Button>
                            )}
                            {project.status === StageName.Sv2Proposed && (
                              <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white mt-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSv2ApprovalProject(project);
                                  setSv2ApprovalOpen(true);
                                }}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Approve SV2
                              </Button>
                            )}
                            {project.status === StageName.PendingOemSignoff && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white mt-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenSignoff(project);
                                }}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Sign Off
                              </Button>
                            )}
                            {!project.hardwareListUploaded && [
                              StageName.Sv1Scheduled, StageName.Sv1Completed,
                              StageName.Sv2Proposed, StageName.Sv2Scheduled, StageName.Sv2Completed,
                              StageName.InstallationScheduled, StageName.InstallationInProgress,
                              StageName.InstallationCompleted, StageName.ClientReview, StageName.PendingOemSignoff,
                              StageName.PendingAdminClosure, StageName.ProjectCompleted,
                              'SV1 In Progress', 'SV2 In Progress'
                            ].includes(project.status) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100 mt-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/projects/${project.id}/view?tab=documents`);
                                  }}
                                >
                                  <Wrench className="w-4 h-4 mr-1" />
                                  Hardware List Pending
                                </Button>
                              )}
                          </div>
                          {canDeleteProject(project) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                setProjectToDelete(project);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {/* Show progress bar for in-progress projects */}
                      {project.status !== StageName.ProjectCompleted &&
                        project.status !== StageName.Cancelled &&
                        project.status !== StageName.OnHold &&
                        completion !== null && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-gray-600">Progress</span>
                              <span className="font-medium text-purple-600">{completion}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${completion}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      {project.installerTeam && (
                        <p className="text-sm text-gray-500 mt-2">Team: {project.installerTeam}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Quote/Schedule Changes</DialogTitle>
            <DialogDescription>
              Admin has requested approval for changes to {projectForApproval?.clientName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900">{projectForApproval?.clientName}</h4>
              <p className="text-sm text-gray-500">{projectForApproval?.location} • {projectForApproval?.sqft} sq.ft</p>
            </div>

            {projectForApproval?.revisedQuote && (
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm font-medium text-orange-700">Revised Quote</p>
                <p className="text-2xl font-bold text-orange-600">
                  ₹{projectForApproval?.revisedQuote?.toLocaleString()}
                </p>
                {projectForApproval?.originalQuote && (
                  <p className="text-sm text-gray-500 line-through">
                    Original: ₹{projectForApproval?.originalQuote?.toLocaleString()}
                  </p>
                )}
              </div>
            )}

            {projectForApproval?.scheduledDate && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-700">New Scheduled Date</p>
                <p className="text-lg font-semibold text-blue-600">{projectForApproval?.scheduledDate}</p>
              </div>
            )}

            {projectForApproval?.scheduleChangeReason && (
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm font-medium text-yellow-700">Reason for Change</p>
                <p className="text-gray-700">{projectForApproval?.scheduleChangeReason}</p>
              </div>
            )}

            {projectForApproval?.reworkDetails && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm font-medium text-purple-700">Rework Details</p>
                <p className="text-gray-700">{projectForApproval?.reworkDetails}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                onClick={() => handleApproveChanges(false)}
                data-testid="reject-changes-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => handleApproveChanges(true)}
                data-testid="approve-changes-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Approve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Project Detail Dialog */}
      <ProjectDetailModal
        open={projectDetailOpen}
        onOpenChange={setProjectDetailOpen}
        project={selectedProjectDetail}
        getStatusColor={getStatusColor}
      />

      {/* Quotation Approval Dialog */}
      <QuotationApprovalDialog
        open={quotationApprovalOpen}
        onOpenChange={setQuotationApprovalOpen}
        quotation={selectedQuotation}
        projectId={selectedQuotation?.projectId}
        showFullDetails={true}
        onApprovalComplete={() => {
          setQuotationApprovalOpen(false);
          setSelectedQuotation(null);
          fetchProjects();
        }}
      />

      {/* SV2 Approval Dialog */}
      <Sv2ApprovalDialog
        open={sv2ApprovalOpen}
        onOpenChange={setSv2ApprovalOpen}
        project={sv2ApprovalProject}
        onApprovalComplete={() => {
          setSv2ApprovalOpen(false);
          setSv2ApprovalProject(null);
          fetchProjects();
        }}
      />

      {/* Sign-Off Dialog */}
      <Dialog open={signoffDialogOpen} onOpenChange={setSignoffDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-pink-600" />
              Project Sign-Off
            </DialogTitle>
            <DialogDescription>
              Review the client review checklist and approve sign-off for {signoffProject?.clientName}
            </DialogDescription>
          </DialogHeader>

          {signoffLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : signoffDetails ? (
            <div className="space-y-6 mt-4">
              {/* Project Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900">{signoffDetails.clientName}</h4>
                <p className="text-sm text-gray-500">{signoffDetails.siteAddress} • {signoffDetails.sqft} sq.ft</p>
                {signoffDetails.supervisorName && (
                  <p className="text-sm text-gray-500">Supervisor: {signoffDetails.supervisorName}</p>
                )}
              </div>

              {/* Client Review Summary */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Client Review Summary</h4>
                <div className="space-y-4">
                  {signoffDetails.groups?.map((group, gi) => (
                    <div key={gi}>
                      <p className="text-sm font-medium text-gray-700 mb-2">{group.groupName}</p>
                      <div className="space-y-2">
                        {group.items?.map((item) => (
                          <div key={item.id} className="p-3 bg-white border rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {item.isApproved ? (
                                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                                )}
                                <div>
                                  <span className="text-sm font-medium text-gray-900">{item.itemName}</span>
                                  {item.isRequired && <span className="ml-1 text-xs text-red-500">*</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                {item.photoCount > 0 && (
                                  <button
                                    className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800"
                                    onClick={() => loadSignoffItemPhotos(signoffProject.id, item.id)}
                                  >
                                    📷 {signoffPhotosExpanded[item.id] ? 'Hide' : 'View'} Photos ({item.photoCount})
                                  </button>
                                )}
                                {item.approvedBy && (
                                  <span className="text-green-600">✓ {item.approvedBy}</span>
                                )}
                              </div>
                            </div>
                            {item.notes && (
                              <p className="text-xs text-gray-500 mt-1 ml-6">
                                <span className="font-medium">Notes:</span> {item.notes}
                              </p>
                            )}
                            {/* Photo thumbnails */}
                            {signoffPhotosExpanded[item.id] && signoffItemPhotos[item.id] && (
                              <div className="mt-2 ml-6 flex flex-wrap gap-2">
                                {signoffItemPhotos[item.id].map((photo) => (
                                  <img
                                    key={photo.id}
                                    src={photo.url}
                                    alt={photo.fileName}
                                    className="w-20 h-20 object-cover rounded border cursor-pointer hover:opacity-80"
                                    onClick={() => setSignoffEnlargedPhoto(photo)}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-sm text-gray-600">
                  {signoffDetails.completedItems}/{signoffDetails.totalItems} items approved
                </div>
              </div>

              {/* Rating */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Rating <span className="text-red-500">*</span></h4>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setSignoffRating(star)}
                      onMouseEnter={() => setSignoffHoverRating(star)}
                      onMouseLeave={() => setSignoffHoverRating(0)}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-8 h-8 ${(signoffHoverRating || signoffRating) >= star
                          ? 'text-yellow-500 fill-yellow-500'
                          : 'text-gray-300'
                          }`}
                      />
                    </button>
                  ))}
                  {signoffRating > 0 && (
                    <span className="ml-2 text-sm font-medium text-gray-600">{signoffRating}/5</span>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Notes (optional)</h4>
                <textarea
                  value={signoffNotes}
                  onChange={(e) => setSignoffNotes(e.target.value)}
                  placeholder="Add any notes about the project..."
                  className="w-full p-3 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  rows={3}
                />
              </div>

              {/* Approve Button */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setSignoffDialogOpen(false)}
                  disabled={signoffSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleApproveSignoff}
                  disabled={signoffSubmitting || signoffRating < 1}
                >
                  {signoffSubmitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Approving...</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Approve Sign-Off</>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">Failed to load details</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Enlarged Photo Dialog */}
      {signoffEnlargedPhoto && (
        <Dialog open={!!signoffEnlargedPhoto} onOpenChange={() => setSignoffEnlargedPhoto(null)}>
          <DialogContent className="max-w-[95vw] sm:max-w-3xl p-2">
            <DialogHeader>
              <DialogTitle className="text-sm">{signoffEnlargedPhoto.fileName}</DialogTitle>
            </DialogHeader>
            <img
              src={signoffEnlargedPhoto.url}
              alt={signoffEnlargedPhoto.fileName}
              className="w-full max-h-[75vh] object-contain rounded"
            />
          </DialogContent>
        </Dialog>
      )}
    </div >
  );
};

export default EnhancedDashboard;
